import { Subscribable } from "../../adapters/Subscribable";
import type { initialState } from "./initialState";
import type { RemoteConfigAdapter } from "./FirebaseRemoteConfigAdapter";

export type FeatureFlagName = keyof typeof initialState;

export type FlagConfiguration = {
  name: string;
  description: string;
  value: boolean;
  flag: FeatureFlagName;
};

export type ConfigStateItem = {
  name: string;
  description: string;
  defaultValue: boolean;
  value?: boolean;
  hidden?: boolean;
};

export type FlagState = Record<string, ConfigStateItem>;

export class FeatureFlagManager extends Subscribable<FlagState> {
  private remoteConfigAdapter?: RemoteConfigAdapter;

  constructor(
    initialState: FlagState,
    private readonly configRepository?: {
      getState: () => Record<FeatureFlagName, boolean>;
      setState: (config: Record<FeatureFlagName, boolean>) => void;
    },
    remoteConfigAdapter?: RemoteConfigAdapter,
  ) {
    const state = structuredClone(initialState);

    if (configRepository) {
      const persistedState = configRepository.getState();
      Object.keys(initialState).forEach((key) => {
        if (persistedState[key as FeatureFlagName] !== undefined) {
          state[key as FeatureFlagName]!.value =
            persistedState[key as FeatureFlagName];
        }
      });
    }
    super(state);
    this.remoteConfigAdapter = remoteConfigAdapter;
  }

  async initializeRemoteConfig(): Promise<void> {
    if (this.remoteConfigAdapter) {
      await this.remoteConfigAdapter.initialize();
      this.loadRemoteValues();
    }
  }

  private loadRemoteValues(): void {
    if (!this.remoteConfigAdapter) {
      return;
    }

    const remoteValues = this.remoteConfigAdapter.getAllRemoteValues();

    this.updateState((draft) => {
      Object.entries(remoteValues).forEach(([flag, remoteValue]) => {
        const config = draft[flag as keyof typeof draft] as ConfigStateItem;
        if (config && config.value === undefined) {
          // Only set remote value if there's no local override
          config.defaultValue = remoteValue;
        }
      });
    });
  }

  getConfig(flag: FeatureFlagName): FlagConfiguration {
    const config = this.getState()[flag];
    if (!config) {
      throw new Error(`Configuration with flag "${flag}" not found.`);
    }

    // Priority order: local override > remote config > original default
    let finalValue = config.defaultValue;

    if (this.remoteConfigAdapter) {
      const remoteValue = this.remoteConfigAdapter.getRemoteValue(flag);
      if (remoteValue !== undefined) {
        finalValue = remoteValue;
      }
    }

    // Local override always takes precedence
    if (config.value !== undefined) {
      finalValue = config.value;
    }

    return {
      name: config.name,
      description: config.description,
      value: finalValue,
      flag,
    };
  }

  setConfig(flag: FeatureFlagName, value: boolean): void {
    this.updateState((draft) => {
      const key = flag as keyof typeof draft;
      if (key in draft) {
        (draft[key] as ConfigStateItem).value = value;
      } else {
        throw new Error(`Configuration with flag "${flag}" not found.`);
      }
    });
    if (this.configRepository) {
      const persistedState = this.configRepository.getState();
      persistedState[flag] = value;
      this.configRepository.setState(persistedState);
    }
  }

  resetDefaults = (): void => {
    this.updateState((draft) => {
      Object.keys(draft).forEach((flag) => {
        const config = draft[flag as keyof typeof draft] as ConfigStateItem;
        if (config) {
          delete config.value;
        }
      });
    });
    if (this.configRepository) {
      this.configRepository.setState({} as Record<FeatureFlagName, boolean>);
    }
  };
}
